/**
 * SCR-006 — Branch detail + rooms list inline.
 * Admin only: view/edit branch info, manage rooms for this branch.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { AlertCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@client/components/data-table';
import { BranchForm, type BranchFormValues } from '@client/components/forms/branch-form';
import { RoomForm, type RoomFormValues } from '@client/components/forms/room-form';
import { isApiError, useBranch, useUpdateBranch } from '@client/lib/queries/use-branches';
import {
  type Room,
  roomKeys,
  useCreateRoom,
  useDeleteRoom,
  useRooms,
} from '@client/lib/queries/use-rooms';
// Note: useDeleteRoom is used in RoomDeleteButton (component-level hook call — valid)
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/branches/$branchId')({
  component: BranchDetailPage,
});

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: 'Hoạt động', className: 'bg-brand-green-100 text-brand-green-700' },
  inactive: { label: 'Tạm dừng', className: 'bg-secondary text-muted-foreground' },
  closed: { label: 'Đã đóng', className: 'bg-destructive/10 text-destructive' },
};

function BranchDetailPage() {
  const { branchId } = Route.useParams();

  const { data: branch, isLoading, isError } = useBranch(branchId);
  const { data: roomsData, isLoading: roomsLoading } = useRooms({ branchId, pageSize: 100 });
  const rooms = roomsData?.items ?? [];

  const updateBranch = useUpdateBranch(branchId);
  const createRoom = useCreateRoom();
  const qc = useQueryClient();

  const [branchDialog, setBranchDialog] = useState(false);
  const [branchFormError, setBranchFormError] = useState<string | null>(null);
  const [blockingClasses, setBlockingClasses] = useState<{ code: string; name: string }[]>([]);

  const [roomDialog, setRoomDialog] = useState(false);
  const [editRoom, setEditRoom] = useState<Room | null>(null);
  const [roomFormError, setRoomFormError] = useState<string | null>(null);

  async function handleBranchSubmit(values: BranchFormValues) {
    setBranchFormError(null);
    setBlockingClasses([]);
    try {
      await updateBranch.mutateAsync(values);
      toast.success('Đã cập nhật cơ sở.');
      setBranchDialog(false);
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const body = err.body as {
          message?: string;
          blockingClasses?: { code: string; name: string }[];
        };
        setBranchFormError(body?.message ?? 'Không thể thực hiện thao tác này.');
        setBlockingClasses(body?.blockingClasses ?? []);
      } else {
        setBranchFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    }
  }

  async function handleRoomSubmit(values: RoomFormValues) {
    setRoomFormError(null);
    try {
      if (editRoom) {
        // Use direct API call to avoid hook-in-callback violation
        await api.patch(`/api/rooms/${editRoom.id}`, values);
        await qc.invalidateQueries({ queryKey: roomKeys.all });
        toast.success('Đã cập nhật phòng.');
      } else {
        await createRoom.mutateAsync({ ...values, branchId });
        toast.success('Đã thêm phòng.');
      }
      setRoomDialog(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setRoomFormError('Tên phòng đã tồn tại trong cơ sở này.');
      } else {
        setRoomFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    }
  }

  if (isLoading) return <BranchDetailSkeleton />;

  if (isError || !branch) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Không tải được thông tin cơ sở.{' '}
          <Link to="/admin/branches" className="underline">Quay lại</Link>
        </AlertDescription>
      </Alert>
    );
  }

  const statusMeta = STATUS_LABELS[branch.status] ?? STATUS_LABELS['active'];

  const roomColumns: ColumnDef<Room>[] = [
    {
      accessorKey: 'name',
      header: 'Phòng',
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'capacity',
      header: 'Sức chứa',
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge className="bg-brand-green-100 text-brand-green-700 border-0 text-xs">Đang dùng</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Đã tắt</Badge>
        ),
    },
    {
      accessorKey: 'note',
      header: 'Ghi chú',
      cell: ({ row }) => row.original.note ?? '—',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => { setEditRoom(row.original); setRoomFormError(null); setRoomDialog(true); }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <RoomDeleteButton roomId={row.original.id} />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/branches">Cơ sở</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{branch.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-foreground">{branch.name}</h1>
          <Badge className={`${statusMeta?.className ?? ''} border-0`}>{statusMeta?.label}</Badge>
        </div>
        <Button
          variant="default"
          className="self-start sm:self-auto"
          onClick={() => { setBranchFormError(null); setBlockingClasses([]); setBranchDialog(true); }}
        >
          <Pencil className="h-4 w-4 mr-1.5" /> Sửa
        </Button>
      </div>

      {/* Branch info card */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <InfoRow label="Mã cơ sở" value={branch.code} mono />
            <InfoRow
              label="Khai trương"
              value={branch.openingDate ? format(new Date(branch.openingDate), 'dd/MM/yyyy') : '—'}
            />
            <InfoRow label="Địa chỉ" value={branch.address} />
            <InfoRow label="SĐT" value={branch.phone ?? '—'} />
            <InfoRow label="Email" value={branch.email ?? '—'} />
          </div>
        </CardContent>
      </Card>

      {/* Rooms section */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold">
            Phòng học ({rooms.length})
          </h2>
          <Button
            className="bg-accent text-accent-foreground hover:bg-accent/90 h-9"
            size="sm"
            onClick={() => { setEditRoom(null); setRoomFormError(null); setRoomDialog(true); }}
          >
            <Plus className="h-4 w-4 mr-1" /> Thêm phòng
          </Button>
        </div>

        <DataTable
          columns={roomColumns}
          data={rooms}
          isLoading={roomsLoading}
          emptyMessage="Chưa có phòng học nào."
        />
      </div>

      {/* Branch edit dialog */}
      <Dialog open={branchDialog} onOpenChange={setBranchDialog}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sửa cơ sở</DialogTitle>
          </DialogHeader>
          {branchFormError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>
                {branchFormError}
                {blockingClasses.length > 0 && (
                  <ul className="mt-2 list-disc pl-4 text-xs space-y-0.5">
                    {blockingClasses.map((c) => (
                      <li key={c.code}>
                        <span className="font-mono">{c.code}</span> — {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
          <BranchForm
            isEdit
            isSubmitting={updateBranch.isPending}
            defaultValues={{
              code: branch.code,
              name: branch.name,
              address: branch.address,
              phone: branch.phone ?? '',
              email: branch.email ?? '',
              status: branch.status,
              openingDate: branch.openingDate ?? '',
            }}
            onSubmit={handleBranchSubmit}
            onCancel={() => setBranchDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Room create/edit dialog */}
      <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRoom ? 'Sửa phòng' : 'Thêm phòng'}</DialogTitle>
          </DialogHeader>
          {roomFormError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{roomFormError}</AlertDescription>
            </Alert>
          )}
          <RoomForm
            isEdit={!!editRoom}
            isSubmitting={createRoom.isPending}
            fixedBranchId={branchId}
            defaultValues={
              editRoom
                ? {
                    branchId: editRoom.branchId,
                    name: editRoom.name,
                    capacity: editRoom.capacity,
                    isActive: editRoom.isActive,
                    note: editRoom.note ?? '',
                  }
                : undefined
            }
            onSubmit={handleRoomSubmit}
            onCancel={() => setRoomDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoomDeleteButton({ roomId }: { roomId: string }) {
  const deleteRoom = useDeleteRoom(roomId);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-destructive hover:text-destructive"
      disabled={deleteRoom.isPending}
      onClick={async () => {
        if (!confirm('Xóa phòng này?')) return;
        try {
          await deleteRoom.mutateAsync();
          toast.success('Đã xóa phòng.');
        } catch (err) {
          if (isApiError(err) && err.status === 409) {
            toast.error('Phòng đang được dùng, không thể xóa. Hãy tắt phòng thay vì xóa.');
          } else {
            toast.error('Không thể xóa phòng, thử lại sau.');
          }
        }
      }}
    >
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
      <span className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function BranchDetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-8 w-56" />
      <Card>
        <div className="p-4"><Skeleton className="h-4 w-32" /></div>
        <CardContent className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent>
      </Card>
    </div>
  );
}
